import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import {
    deleteOrganizationMedicalFee,
    getOrganizationManagementProducts,
    getOrganizationManagementStaff,
    getOrganizationMedicalFees,
    postOrganizationMedicalFee,
    putOrganizationMedicalFee,
} from '../store/user/thunks';

const { Text } = Typography;
const { TextArea } = Input;

type AnyRecord = Record<string, any>;

interface Props {
    organizationId: string;
    getOrganizationMedicalFees?: any;
    postOrganizationMedicalFee?: any;
    putOrganizationMedicalFee?: any;
    deleteOrganizationMedicalFee?: any;
    getOrganizationManagementStaff?: any;
    getOrganizationManagementProducts?: any;
}

interface RuleRow {
    id: string;
    userId: string;
    userName: string;
    userEmail?: string;
    organizationProductId?: string;
    productName?: string;
    productCode?: string;
    scope: string;
    feeType: string;
    feeValue: number;
    referencePrice?: number | null;
    calculatedAmount?: number | null;
    status: string;
    observation?: string;
}

const valueOf = (source: AnyRecord, ...keys: string[]) => {
    for (const key of keys) {
        if (source && source[key] !== undefined && source[key] !== null) return source[key];
    }
    return undefined;
};

const listOf = (source: any, ...keys: string[]): any[] => {
    if (Array.isArray(source)) return source;
    const value = valueOf(source || {}, ...keys);
    return Array.isArray(value) ? value : [];
};

const normalizeRules = (source: any): RuleRow[] => listOf(source, 'items', 'Items', 'data', 'Data', 'rows', 'Rows', 'rules', 'Rules').map((item: AnyRecord) => ({
    id: String(valueOf(item, 'id', 'Id') || ''),
    userId: String(valueOf(item, 'userId', 'UserId') || ''),
    userName: String(valueOf(item, 'userName', 'UserName') || valueOf(item, 'userEmail', 'UserEmail') || 'Usuario'),
    userEmail: valueOf(item, 'userEmail', 'UserEmail'),
    organizationProductId: valueOf(item, 'organizationProductId', 'OrganizationProductId'),
    productName: valueOf(item, 'productName', 'ProductName'),
    productCode: valueOf(item, 'productCode', 'ProductCode'),
    scope: String(valueOf(item, 'scope', 'Scope') || 'GLOBAL').toUpperCase(),
    feeType: String(valueOf(item, 'feeType', 'FeeType') || 'PORCENTAJE').toUpperCase(),
    feeValue: Number(valueOf(item, 'feeValue', 'FeeValue') || 0),
    referencePrice: valueOf(item, 'referencePrice', 'ReferencePrice'),
    calculatedAmount: valueOf(item, 'calculatedAmount', 'CalculatedAmount'),
    status: String(valueOf(item, 'status', 'Status') || 'ACTIVO').toUpperCase(),
    observation: valueOf(item, 'observation', 'Observation'),
})).filter(row => row.id && row.userId);

const normalizeUsers = (source: any): AnyRecord[] => listOf(source, 'items', 'Items', 'data', 'Data', 'rows', 'Rows', 'staff', 'Staff', 'users', 'Users');
const normalizeProducts = (source: any): AnyRecord[] => listOf(source, 'items', 'Items', 'data', 'Data', 'rows', 'Rows', 'products', 'Products');

const displayUser = (item: AnyRecord) => String(valueOf(item, 'fullName', 'FullName', 'name', 'Name', 'userName', 'UserName') || valueOf(item, 'email', 'Email') || 'Usuario');
const displayProduct = (item: AnyRecord) => {
    const code = valueOf(item, 'code', 'Code', 'productCode', 'ProductCode');
    const name = valueOf(item, 'name', 'Name', 'description', 'Description', 'productName', 'ProductName');
    return [code, name].filter(Boolean).join(' · ') || 'Producto';
};

const OrganizationMedicalFeeManagement: React.FC<Props> = ({
    organizationId,
    getOrganizationMedicalFees: loadFees,
    postOrganizationMedicalFee: createFee,
    putOrganizationMedicalFee: updateFee,
    deleteOrganizationMedicalFee: inactivateFee,
    getOrganizationManagementStaff: loadStaff,
    getOrganizationManagementProducts: loadProducts,
}) => {
    const [rules, setRules] = useState<RuleRow[]>([]);
    const [users, setUsers] = useState<AnyRecord[]>([]);
    const [products, setProducts] = useState<AnyRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<RuleRow | null>(null);
    const [scope, setScope] = useState('GLOBAL');
    const [form] = Form.useForm();

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [feeResponse, userResponse, productResponse] = await Promise.all([
                loadFees?.(organizationId, {}),
                loadStaff?.(organizationId),
                loadProducts?.(organizationId),
            ]);
            setRules(normalizeRules(feeResponse));
            setUsers(normalizeUsers(userResponse));
            setProducts(normalizeProducts(productResponse));
        } catch (loadError) {
            setError(loadError instanceof Error ? loadError.message : 'No se pudo cargar la configuración de honorarios.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [organizationId]);

    const userOptions = useMemo(() => users.map(user => ({ value: String(valueOf(user, 'userId', 'UserId', 'id', 'Id') || ''), label: displayUser(user) }))
        .filter(option => option.value), [users]);
    const productOptions = useMemo(() => products.map(product => ({ value: String(valueOf(product, 'organizationProductId', 'OrganizationProductId', 'id', 'Id') || ''), label: displayProduct(product) }))
        .filter(option => option.value), [products]);

    const openCreate = () => {
        setEditing(null);
        setScope('GLOBAL');
        form.resetFields();
        form.setFieldsValue({ scope: 'GLOBAL', feeType: 'PORCENTAJE', status: 'ACTIVO' });
        setModalOpen(true);
    };

    const openEdit = (row: RuleRow) => {
        setEditing(row);
        setScope(row.scope);
        form.setFieldsValue({
            userId: row.userId,
            organizationProductId: row.organizationProductId,
            scope: row.scope,
            feeType: row.feeType,
            feeValue: row.feeValue,
            status: row.status,
            observation: row.observation,
        });
        setModalOpen(true);
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                userId: values.userId,
                organizationProductId: values.scope === 'PRODUCTO' ? values.organizationProductId : null,
                scope: values.scope,
                feeType: values.scope === 'GLOBAL' ? 'PORCENTAJE' : values.feeType,
                feeValue: values.feeValue,
                status: values.status || 'ACTIVO',
                observation: values.observation || null,
            };
            const response = editing
                ? await updateFee?.(organizationId, editing.id, payload)
                : await createFee?.(organizationId, payload);
            if (response !== undefined) setRules(normalizeRules(response));
            setModalOpen(false);
        } catch (saveError: any) {
            if (saveError?.errorFields) return;
            message.error(saveError instanceof Error ? saveError.message : 'No se pudo guardar el honorario.');
        }
    };

    const inactivate = async (row: RuleRow) => {
        try {
            const response = await inactivateFee?.(organizationId, row.id);
            if (response !== undefined) setRules(normalizeRules(response));
        } catch (saveError) {
            message.error(saveError instanceof Error ? saveError.message : 'No se pudo inactivar el honorario.');
        }
    };

    const columns = [
        { title: 'Profesional', key: 'user', render: (_: unknown, row: RuleRow) => <Space direction="vertical" size={0}><Text strong>{row.userName}</Text><Text type="secondary">{row.userEmail || row.userId}</Text></Space> },
        { title: 'Alcance', dataIndex: 'scope', key: 'scope', render: (value: string) => <Tag color={value === 'GLOBAL' ? 'blue' : 'purple'}>{value === 'GLOBAL' ? 'Todos los productos' : 'Producto'}</Tag> },
        { title: 'Producto', key: 'product', render: (_: unknown, row: RuleRow) => row.scope === 'GLOBAL' ? <Text type="secondary">Global</Text> : <Text>{[row.productCode, row.productName].filter(Boolean).join(' · ') || row.organizationProductId}</Text> },
        { title: 'Honorario', key: 'fee', render: (_: unknown, row: RuleRow) => <Text>{row.feeValue.toFixed(2)} {row.feeType === 'PORCENTAJE' ? '%' : 'USD'}</Text> },
        { title: 'Estimado', key: 'amount', render: (_: unknown, row: RuleRow) => row.calculatedAmount == null ? <Text type="secondary">Se calcula por producto</Text> : `$${Number(row.calculatedAmount).toFixed(2)}` },
        { title: 'Estado', dataIndex: 'status', key: 'status', render: (value: string) => <Tag color={value === 'ACTIVO' ? 'green' : 'default'}>{value}</Tag> },
        { title: 'Acciones', key: 'actions', render: (_: unknown, row: RuleRow) => <Space><Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>Editar</Button>{row.status === 'ACTIVO' && <Popconfirm title="¿Inactivar este honorario?" onConfirm={() => inactivate(row)}><Button danger size="small" icon={<DeleteOutlined />}>Inactivar</Button></Popconfirm>}</Space> },
    ];

    return <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card title="Honorarios médicos" extra={<Space><Button icon={<ReloadOutlined />} onClick={loadData}>Actualizar</Button><Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Nuevo honorario</Button></Space>}>
            <Alert showIcon type="info" message="Reglas por usuario" description="Un honorario global se aplica a todos los productos del profesional. Un honorario por producto permite fijar un valor o porcentaje; el backend valida que nunca supere el precio vigente del producto." />
        </Card>
        {error && <Alert showIcon type="error" message="No se pudo cargar la configuración" description={error} closable onClose={() => setError(null)} />}
        <Card>
            {loading ? <div style={{ textAlign: 'center', padding: 36 }}><Spin /></div> : rules.length === 0 ? <Empty description="No hay honorarios configurados" /> : <Table rowKey="id" columns={columns} dataSource={rules} pagination={{ pageSize: 10 }} scroll={{ x: 1100 }} />}
        </Card>
        <Modal title={editing ? 'Editar honorario médico' : 'Nuevo honorario médico'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={save} okText="Guardar" width={620} destroyOnClose>
            <Alert showIcon type="info" message="El profesional se registra contra usr.user" description="Los honorarios se guardan por organización y usuario. El alcance global solo admite porcentaje; el alcance por producto admite porcentaje o valor fijo." style={{ marginBottom: 16 }} />
            <Form form={form} layout="vertical">
                <Form.Item label="Profesional" name="userId" rules={[{ required: true, message: 'Selecciona el profesional.' }]}><Select showSearch optionFilterProp="label" options={userOptions} placeholder="Selecciona un usuario" /></Form.Item>
                <Form.Item label="Alcance" name="scope" rules={[{ required: true }]}><Select options={[{ value: 'GLOBAL', label: 'Global: todos los productos' }, { value: 'PRODUCTO', label: 'Producto específico' }]} onChange={(value) => { setScope(value); if (value === 'GLOBAL') form.setFieldValue('feeType', 'PORCENTAJE'); }} /></Form.Item>
                {scope === 'PRODUCTO' && <Form.Item label="Producto de la organización" name="organizationProductId" rules={[{ required: true, message: 'Selecciona el producto.' }]}><Select showSearch optionFilterProp="label" options={productOptions} placeholder="Selecciona un producto" /></Form.Item>}
                <Form.Item label="Tipo de honorario" name="feeType" rules={[{ required: true }]}><Select disabled={scope === 'GLOBAL'} options={[{ value: 'PORCENTAJE', label: 'Porcentaje (%)' }, { value: 'VALOR_FIJO', label: 'Valor fijo (USD)' }]} /></Form.Item>
                <Form.Item label={scope === 'GLOBAL' ? 'Porcentaje global' : 'Valor o porcentaje'} name="feeValue" rules={[{ required: true, message: 'Ingresa el honorario.' }, { type: 'number', min: 0, max: scope === 'GLOBAL' ? 100 : undefined, message: scope === 'GLOBAL' ? 'El porcentaje debe estar entre 0 y 100.' : 'El valor no puede ser negativo.' }]}><InputNumber min={0} max={scope === 'GLOBAL' ? 100 : undefined} precision={2} style={{ width: '100%' }} /></Form.Item>
                <Form.Item label="Estado" name="status"><Select options={[{ value: 'ACTIVO', label: 'Activo' }, { value: 'INACTIVO', label: 'Inactivo' }]} /></Form.Item>
                <Form.Item label="Observación" name="observation"><TextArea rows={3} maxLength={500} showCount /></Form.Item>
            </Form>
        </Modal>
    </Space>;
};

export default connect(null, {
    deleteOrganizationMedicalFee,
    getOrganizationManagementProducts,
    getOrganizationManagementStaff,
    getOrganizationMedicalFees,
    postOrganizationMedicalFee,
    putOrganizationMedicalFee,
})(OrganizationMedicalFeeManagement);
